import React, { Dispatch, SetStateAction } from 'react';

import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { JiraTemplateChoice } from '../../../hooks/use-jira-template-choice.tsx';
import { UserSegmentationTeamTypes } from '../../../models/user-segmentation';
import { TemplateOptionMetadata } from '../template-definitions';

interface CardProps {
	imageSrc: string;
	mobileImgSrc: string;
	title: string;
	subtext: string;
	templateType: JiraTemplateChoice;
	templateId: string;
	setSelectedTemplate: Dispatch<SetStateAction<JiraTemplateChoice>>;
	setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
	selectedCardTitle: string | undefined;
	setSelectedCardTitle: Dispatch<SetStateAction<string | undefined>>;
	teamType: UserSegmentationTeamTypes;
	templateMetadata?: TemplateOptionMetadata;
	setSelectedTemplateMetadata: Dispatch<SetStateAction<TemplateOptionMetadata | undefined>>;
}

const cardBorderStyle = css({
	margin: '1px',
	padding: '0px',
	borderRadius: 'var(--rounding, 8px)',
	// eslint-disable-next-line local/ensure-wac-token-usage
	border: '1px solid #C1C7D0',
	background: wacToken('color.neutral.light0'),
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'row',
	maxWidth: '463px',
	alignItems: 'center',
	cursor: 'pointer',
	['&:hover']: {
		boxShadow:
			// eslint-disable-next-line local/ensure-wac-token-usage
			'0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)',
	},
	[media.above.md]: {
		flexDirection: 'column',
		justifyContent: 'flex-start',
		maxWidth: 'none',
		minHeight: '333px',
		flex: 1,
	},
});

const cardTitleStyle = css({
	fontFamily: token('font.family.heading'),
	color: token('color.text'),
	fontSize: '1.25rem',
	fontWeight: token('font.weight.bold'),
	lineHeight: '1.75rem',
	margin: 0,
	[media.above.md]: {
		fontSize: '20px',
		lineHeight: '28px',
	},
});

const cardDescriptionVisualUpdateStyle = css({
	fontFamily: token('font.family.body'),
	fontWeight: token('font.weight.regular'),
	// eslint-disable-next-line local/ensure-wac-token-usage
	fontSize: '1rem',
	// eslint-disable-next-line local/ensure-wac-token-usage
	lineHeight: '1.5rem',
	margin: 0,
});

const textContainerVisualFeedbackStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& p']: {
		color: token('color.text'),
	},
});

const cardBorderVisualFeedbackStyle = css({
	margin: 0,
	border: 'none',
});

const cardBorderUnselectedVisualFeedbackStyle = css({
	outline: `1px solid ${token('color.background.accent.gray.subtlest')}`,
	borderRadius: '8px',
});

const cardBorderSelectedVisualFeedbackStyle = css({
	outline: `2px solid ${token('color.link')}`,
	borderRadius: '8px',
});

const titleContainerStyle = css({
	display: 'flex',
	alignItems: 'center',
});

const cardBorderUnselectedStyle = css({
	borderRadius: '8px',
	border: `1px solid ${token('color.background.accent.gray.subtlest')}`,
	margin: 0,
});

const cardBorderSelectedStyle = css({
	borderRadius: '8px',
	border: `2px solid ${token('color.link')}`,
});

const textContainerStyle = css({
	alignItems: 'flex-start',
	fontFamily: wacToken('font.charlie.text'),
	padding: token('space.300'),
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	justifyContent: 'space-between',
	textAlign: 'start',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& h3, & p']: {
		margin: 0,
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& p']: {
		color: wacToken('color.primary.black'),
	},
	[media.above.md]: {
		padding: token('space.300'),
		justifyContent: 'flex-start',
		flexGrow: 1,
	},
});

const imgStyle = css({
	display: 'none',
	[media.above.md]: {
		display: 'block',
		width: '100%',
		height: 'auto',
		flexBasis: 'auto',
	},
});

const mobileImgStyle = css({
	width: '80px',
	height: '100px',
	alignSelf: 'center',
	[media.above.md]: {
		display: 'none',
	},
});

const desktopImageContainer = css({
	display: 'none',
	[media.above.md]: {
		display: 'block',
		marginBlockEnd: token('space.100'),
	},
});

const Card: React.FC<CardProps> = ({
	imageSrc,
	mobileImgSrc,
	title,
	subtext,
	setSelectedTemplate,
	templateType,
	templateId,
	setSelectedTemplateId,
	setSelectedCardTitle,
	selectedCardTitle,
	teamType,
	templateMetadata,
	setSelectedTemplateMetadata,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const visualFeedback = checkFeatureFlagGate(featureFlags.signupVisualAlignmentFeedback);
	return (
		<button
			css={[
				cardBorderStyle,
				visualFeedback && cardBorderVisualFeedbackStyle,
				selectedCardTitle === title
					? visualFeedback
						? cardBorderSelectedVisualFeedbackStyle
						: cardBorderSelectedStyle
					: visualFeedback
						? cardBorderUnselectedVisualFeedbackStyle
						: cardBorderUnselectedStyle,
			]}
			onClick={() => {
				createAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceCard,
					attributes: {
						atlasTicket: 'ATLAS-61649',
						teamType,
						templateType,
						templateId,
						templateName: title,
					},
				}).fire();

				setSelectedTemplate(templateType);
				setSelectedTemplateId(templateId);
				setSelectedCardTitle(title);
				setSelectedTemplateMetadata(templateMetadata);
			}}
		>
			<div css={desktopImageContainer}>
				{/* eslint-disable-next-line no-restricted-syntax */}
				<img src={imageSrc} alt="" css={imgStyle} />
			</div>
			{/* eslint-disable-next-line no-restricted-syntax */}
			<img src={mobileImgSrc} alt="" css={mobileImgStyle} />
			<div css={[textContainerStyle, visualFeedback && textContainerVisualFeedbackStyle]}>
				<div css={titleContainerStyle}>
					<h3 css={cardTitleStyle}>{title}</h3>
				</div>
				<p css={cardDescriptionVisualUpdateStyle}>{subtext}</p>
			</div>
		</button>
	);
};

export default Card;
